import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { ITradeHeader } from '../../../interfaces/trades/trade-header.model';
import { productSegmentToggleFilter } from '../../../shared/filters/product-segmenting/product-segment-toggle-array.filter';
import { IGroupedSelectList } from '../../../shared/models/grouped-select-list.model';
import { ProductSegment } from '../../../shared/models/product-segment.enum';
import { ISelectList } from '../../../shared/models/select-list.model';
import { IToggleable } from '../../../shared/models/toggleable.model';
import { IntercomService } from '../../../shared/services/intercom/intercom.service';
import { MessageService } from '../../../shared/services/message/message.service';
import { SeasonService } from '../../../shared/services/season/season.service';
import { TradesService } from '../../../shared/services/trades/trades.service';
import { UserService } from '../../../shared/services/user/user.service';
import { GatekeeperModalComponent } from '../../modal.component';

@Component({
  selector: 'gk-add-trade-modal',
  templateUrl: './add-trade-modal.html'
})
export class AddTradeModalComponent extends GatekeeperModalComponent<AddTradeModalComponent> {
  header: ITradeHeader;
  newTradingCompanyName: string;
  isMiscTransaction: boolean;
  isStockTransaction: boolean;
  public tradingTypeList: Array<ISelectList> = [];
  public customCashTradeTypeList: Array<ISelectList> = [];
  public stockPeriodList: Array<ISelectList> = [];
  public tradingCompanyList: Array<ISelectList> = [];
  constructor(
    private router: Router,
    private intercomService: IntercomService,
    private messageService: MessageService,
    private tradesService: TradesService,
    private seasonService: SeasonService,
    private userService: UserService,
    public dialogRef: MatDialogRef<AddTradeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();

    this.tradingTypeList = data.locals.tradingTypeList;
    this.stockPeriodList = data.locals.stockPeriodList;
    this.tradingCompanyList = data.locals.tradingCompanyList;

    this.setupForNewTrade();
  }

  addTrade() {
    return this.tradesService
      .addTrade(this.header)
      .then((response: ITradeHeader) => {
        this.intercomService.markToFetchDetails();
        this.messageService.showIcon(MessageTypeEnum.Success);
        this.dialogRef.close(response);
      })
      .catch(() =>
        this.messageService.showMessage(
          MessageTypeEnum.Danger,
          'TRADES_ADD_ERROR'
        )
      );
  }

  onTradingCompanyChange = (option: IGroupedSelectList) => {
    if (option) {
      this.header.tradingCompanyId = option.id;
      this.header.tradingCompanyName = option.value;
    }
  };

  addNewTradingCompany(name: string) {
    this.header.tradingCompanyId = -1;
    this.header.tradingCompanyName = name;
  }

  onTradingTypeChange = (option: IGroupedSelectList) => {
    this.setModalVariables(option.id);
    if (option.id === 1 || option.id === 2) {
      return this.tradesService
        .getCustomCashTradeTypes(option.id)
        .then(
          () =>
            (this.customCashTradeTypeList =
              this.tradesService.customCashTradeTypeList)
        )
        .catch(() => {
          this.messageService.showMessage(
            MessageTypeEnum.Danger,
            'TRADE_TYPES_GET_ERROR'
          );
          this.router.navigate(['/trades']);
        });
    }
    return;
  };

  validateInvoiceNumber() {
    return () =>
      this.tradesService.validateInvoiceNumber(
        this.header.invoiceNumber,
        this.header.businessId,
        this.header.tradingCompanyId
      );
  }

  private setModalVariables(tradeTypeId: number) {
    this.header.tradingTypeId = tradeTypeId;
    switch (tradeTypeId) {
      case 3: // 3 = Stock Purchase Trade Type
      case 4: // 4 = Stock Sale Trade Type
        if (this.isMiscTransaction) {
          this.header.customCashTradeTypeId = undefined;
          this.header.customCashTradeTypeName = undefined;
          this.header.reference = undefined;
          this.header.netValue = undefined;
        }
        this.isMiscTransaction = false;
        this.isStockTransaction = true;
        break;
      case 5: // 5 = Credit Purchase Trade Type
      case 6: // 6 = Credit Sale Trade Type
        if (this.isMiscTransaction) {
          this.header.customCashTradeTypeId = undefined;
          this.header.customCashTradeTypeName = undefined;
          this.header.reference = undefined;
          this.header.netValue = undefined;
        }
        this.isMiscTransaction = false;
        this.isStockTransaction = false;
        break;
      default:
        if (!this.isMiscTransaction) {
          this.header.invoiceNumber = undefined;
          this.header.deliveryDate = undefined;
        }
        this.header.customCashTradeTypeId = undefined;
        this.header.customCashTradeTypeName = undefined;
        this.isMiscTransaction = true;
        this.isStockTransaction = false;
        break;
    }
  }

  private setupForNewTrade() {
    this.isMiscTransaction = false;
    this.isStockTransaction = true;
    let tradingTypeId = 3; // 3 = Stock Purchase Trade Type
    let businessId = 0;

    if (this.userService.userHasMultiBusiness()) {
      businessId = this.userService.user.filters.multiBusinessSelector[0];
    }

    if (businessId === 0 || businessId === -1) {
      businessId = this.userService.user.businessId;
    }

    if (!this.userService.userHasSegment(ProductSegment.Stock)) {
      this.isMiscTransaction = true;
      this.isStockTransaction = false;
      tradingTypeId = 1; // 1 = Miscellaneous Payment Trade Type
      this.tradesService
        .getCustomCashTradeTypes(tradingTypeId)
        .then(
          () =>
            (this.customCashTradeTypeList =
              this.tradesService.customCashTradeTypeList)
        );
    }
    this.header = <ITradeHeader>{
      stockPeriodId: this.setCurrentStockPeriod(businessId),
      businessId: businessId
    };
    this.setModalVariables(tradingTypeId);
    this.tradingTypeList = productSegmentToggleFilter(
      this.userService,
      this.tradingTypeList as IToggleable[]
    ) as ISelectList[];
  }

  private setCurrentStockPeriod(businessId: number): number {
    let currentStockPeriod = null;

    if (businessId > 0) {
      const seasonId = this.seasonService.displaySeasonForOrganisation();

      currentStockPeriod = _.find(
        this.stockPeriodList,
        (stockPeriod: ISelectList) => stockPeriod.id === seasonId
      );
    }

    return currentStockPeriod
      ? currentStockPeriod.id
      : this.stockPeriodList[0].id;
  }
}
